
import {
  BooleanInput,
  DateInput,
  DeleteButton,
  Edit,
  NumberInput,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useRecordContext,
  useGetOne,
  useNotify,
  useRedirect,
} from "react-admin";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import Button from "@mui/material/Button";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AddIcon from "@mui/icons-material/Add";
import { Col, Row } from "../../components/grid.component";
import { Text } from "../../components/typography.component";
import { TextField as MuiTextField } from "@mui/material";
import { API } from "../../providers/api.provider";
import { formatDateOnlyToUtcWithBrtTime } from "../../utils/dateTimezone.util";

const Title = ({ record }: any) => (
  <Text gutterBottom={false}>{record?.username || 'Licença'}</Text>
);

const UserDisplay = () => {
  const record = useRecordContext();
  const { data: user, isLoading } = useGetOne(
    'users',
    { id: record?.userId },
    { enabled: !!record?.userId }
  );
  
  return (
    <MuiTextField
      label="Usuário"
      value={isLoading ? 'Carregando...' : (user?.name || '')}
      disabled
      fullWidth
      variant="filled"
    />
  );
};

const GenerateSerialButton = () => {
  const { setValue } = useFormContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  
  const handleGenerateSerial = async () => {
    setLoading(true);
    try {
      const response = await API.get('/admin/tasks/generate-serial-number');
      const newSerialNumber = response.data.serialNumber;
      setValue('serialNumber', newSerialNumber, { 
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true 
      });
      notify('ra.notification.created', { type: 'success', messageArgs: { smart_count: 1 } });
    } catch (error) {
      notify('ra.notification.http_error', { type: 'error' });
      console.error('Error generating serial number:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Button
      variant="contained"
      color="primary"
      onClick={handleGenerateSerial}
      disabled={loading}
      startIcon={<AutorenewIcon />}
      fullWidth
      style={{ marginTop: '16px' }}
    >
      {loading ? 'Gerando...' : 'Novo'}
    </Button>
  );
};

const CreateShipmentButton = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const licenseId = record?.id;
  
  const handleCreateShipment = () => {
    if (!licenseId) return;
    redirect(`/shipments/create?license_id=${encodeURIComponent(String(licenseId))}`);
  };
  
  return (
    <Button
      variant="contained"
      color="success"
      onClick={handleCreateShipment}
      disabled={!licenseId}
      startIcon={<AddIcon />}
      fullWidth
      style={{ marginTop: '16px' }}
    >
      Adicionar Remessa
    </Button>
  );
};

const formatAcquisitionDate = (value?: string) => {
  if (!value) return '';
  return value.split('T')[0];
};

const applyAcquisitionTime = (data: any) => {
  if (!data.acquisition) return data;
  // Strip any time component — the record may contain a full ISO string (e.g. "2026-02-11T00:00:00.000Z")
  // while a freshly-edited field stores only "YYYY-MM-DD". Normalise to date-only before conversion.
  const dateOnly = data.acquisition.split('T')[0];
  return {
    ...data,
    acquisition: formatDateOnlyToUtcWithBrtTime(dateOnly, 8, 0, 0),
  };
};

const LicenseEditToolbar = () => {
  const notify = useNotify();

  return (
    <Toolbar sx={{ justifyContent: "space-between" }}>
      <SaveButton />
      <DeleteButton
        mutationMode="pessimistic"
        confirmTitle="Excluir licença"
        confirmContent="Tem certeza que deseja excluir esta licença? Esta ação não poderá ser desfeita."
        mutationOptions={{
          onError: (error: any) => {
            const raw = error?.body?.message ?? error?.message ?? "Erro ao excluir licença";
            const message = Array.isArray(raw) ? raw.join(", ") : String(raw);
            notify(message, { type: "error" });
          },
        }}
      />
    </Toolbar>
  );
};

export const LicenseEdit = (props: any) => {
  return (
    <Edit title={<Title />} transform={applyAcquisitionTime} {...props}>
      <SimpleForm toolbar={<LicenseEditToolbar />}>
        <Row spacing={3}>
          <Col sm={4}>
            <UserDisplay />
          </Col>
          <Col sm={4}>
            <ReferenceInput
              label="Produto"
              reference="products"
              source="productId"
            >
              <SelectInput optionText="name" fullWidth />
            </ReferenceInput>
          </Col>
          <Col sm={4}>
            <NumberInput label="Versão" source="version" fullWidth />
          </Col>
        </Row>
        <Row spacing={4}>
          <Col sm={3}>
            <NumberInput label="Tempo de uso" source="usageTime" fullWidth />
          </Col>
          <Col sm={3}>
            <DateInput
              label="Adquirido"
              source="acquisition"
              format={formatAcquisitionDate}
              helperText="Hora fixa 08:00 (BRT)"
              fullWidth
            />
          </Col>
          <Col sm={3}>
            <NumberInput
              label="Ativações contratadas"
              source="contractedActivations"
              fullWidth
            />
          </Col>
          <Col sm={3}>
            <NumberInput
              label="Ativações restantes"
              source="remainingActivations"
              fullWidth
            />
          </Col>
        </Row>
        <Row spacing={2}>
          <Col sm={6}>
            <TextInput
              label="Código do serial"
              source="serialNumber"
              fullWidth
            />
          </Col>
          <Col sm={2}>
            <GenerateSerialButton />
          </Col>
          <Col sm={4}>
            <TextInput label="HWID Hash" source="hwidHash" fullWidth />
          </Col>
        </Row>
        <Row spacing={1}>
          <Col sm={3}>
            <BooleanInput label="Plug" source="plug" fullWidth />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={4}>
            <NumberInput label="Valor Venda" source="valorVenda" fullWidth />
          </Col>
          <Col sm={4}>
            <NumberInput label="Parcelas Vendas" source="parcelasVendas" fullWidth />
          </Col>
          <Col sm={4}>
            <SelectInput 
              label="Método de Pagamento" 
              source="metodoPagamento" 
              fullWidth 
              choices={[
                { id: 'Pix', name: 'Pix' },
                { id: 'Depósito em conta', name: 'Depósito em conta' },
                { id: 'Cartão de Crédito', name: 'Cartão de Crédito' },
                { id: 'Cartão de Débito', name: 'Cartão de Débito' },
                { id: 'Boleto', name: 'Boleto' },
                { id: 'Cheque', name: 'Cheque' },
                { id: 'Empenho', name: 'Empenho' },
              ]}
            />
          </Col>
        </Row>
        <Row spacing={1}>
          <Col sm={12}>
            <TextInput label="Nota Fiscal" source="notaFiscal" fullWidth />
          </Col>
        </Row>
        <Row spacing={1}>
          <Col sm={6}>
            <CreateShipmentButton />
          </Col>
        </Row>
      </SimpleForm>
    </Edit>
  );
};
